import React, { lazy, useState, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Layout from "./components/Layout";
import NotFound from "./pages/404";
import HomePage from "./pages/HomePage";
import ScrollToTop from "./components/ScrollToTop";
import Logo from './assets/images/logo.png';

// Server
const VirtualServer = lazy(() => import("./pages/server/VirtualServer"));
const RyzenServer = lazy(() => import("./pages/server/RyzenServer"));
const MailServer = lazy(() => import("./pages/server/MailServer"));
const DedicatedServer = lazy(() => import("./pages/server/DedicatedServer"));
const GpuServer = lazy(() => import("./pages/server/GpuServer"));
// Ticket
const Ticket = lazy(() => import("./pages/Ticket"));
// Domain
const DomainRegister = lazy(() => import("./pages/domain/DomainRegister"));
const DomainTransfer = lazy(() => import("./pages/domain/DomainTransfer"));
const DomainWhois = lazy(() => import("./pages/domain/DomainWhois"));
// Web Hosting
const WebHosting = lazy(() => import("./pages/hosting/WebHosting"));
const PleskWebHosting = lazy(() => import("./pages/hosting/PleskWebHosting"));
// const WindowsHosting = lazy(() => import("./pages/hosting/WindowsHosting"));
const ResellerHosting = lazy(() => import("./pages/hosting/ResellerHosting"));
const WordpressHosting = lazy(() => import("./pages/hosting/WordpressHosting"));
// Datacenter
const ServerLocation = lazy(() => import("./pages/datacenter/ServerLocation"));
const SubnetLeasing = lazy(() => import("./pages/datacenter/SubnetLeasing"));
// About
const AboutPage = lazy(() => import("./pages/About"));
const BankInformation = lazy(() => import("./pages/BankInformation"));
const Agreements = lazy(() => import("./pages/Agreements"));

function LoadingSpinner() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm transition-all duration-300">
      <div className="relative w-24 h-24">
        <div className="absolute inset-0 animate-spin">
          <img
            src={Logo}
            alt="Creart Cloud Logo"
            className="w-24 h-24 object-contain"
          />
        </div>
        <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-600 dark:text-gray-300">
          Yükleniyor...
        </p>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="animate-pulse flex space-x-4">
        <div className="flex-1 space-y-4 py-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageTransitionLoader() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return isLoading ? <LoadingSpinner /> : null;
}

function AppContent() {
  return (
    <>
      <PageTransitionLoader />
      <ScrollToTop />
      <React.Suspense fallback={<LoadingSkeleton />}>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />

            <Route path="/virtual-server" element={<VirtualServer />} />
            <Route path="/mail-server" element={<MailServer />} />
            <Route path="/ryzen-server" element={<RyzenServer />} />
            <Route path="/gpu-server" element={<GpuServer />} />
            <Route path="/dedicated-server" element={<DedicatedServer />} />

            <Route path="/web-hosting" element={<WebHosting />} />
            <Route path="/plesk-hosting" element={<PleskWebHosting />} />
            <Route path="/reseller-hosting" element={<ResellerHosting />} />
            <Route path="/wordpress-hosting" element={<WordpressHosting />} />

            <Route path="/ticket" element={<Ticket />} />
            <Route path="/about-us" element={<AboutPage />} />
            <Route path="/bank-information" element={<BankInformation />} />
            <Route path="/agreements" element={<Agreements />} />

            <Route path="/domain-register" element={<DomainRegister />} />
            <Route path="/domain-transfer" element={<DomainTransfer />} />
            <Route path="/domain-whois" element={<DomainWhois />} />

            <Route path="/server-location" element={<ServerLocation />} />
            <Route path="/subnet-leasing" element={<SubnetLeasing />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </React.Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}

export default App;